import styled, { css } from 'styled-components';
import { GlobalStyle } from 'styled/global-style';
import SiteFooter from '../home/SiteFooter';
import FooterNavigation from '../home/FooterNavigation';
import Banner from 'components/global/Banner';
import Icon from 'components/global/Icon/Primary';
import { trackEvent } from 'lib/analytics';
import SiteHeader from '../home/site-header';
import TopNavigation from '../home/TopNavigation';
import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserContext } from 'context-providers/user-provider';
import {
  activeMiniSale,
  isMiniSaleBannerHidden,
} from 'lib/mini-sale/mini-sale';
import { getPromoCodeFromQueryParameters } from 'lib/promos';
import { useReferralBanner } from 'hooks/use-referral-banner';
import { useSrpUpgradeBanner } from 'hooks/use-srp-upgrade-banner';
import { SRP_ENROLL_PATH, SRP_PRODUCT_CODE } from 'lib/srp';
import type { FooterDisclaimerConfig } from '../home/FooterNavigation/footer-disclaimer';
import dynamic from 'next/dynamic';
import { mqLargeMax, mqLargeMin } from 'styled/tools/media-queries';

const Cart = dynamic(() => import('components/pages/cart'), { ssr: false });

const routesToHideBanner = ['/checkout', '/join-us'];
const routePrefixesToHideBanner = ['/account/journey/'];

const routesToHideQuizBanner = [
  '/pdp/energy-focus',
  '/pdp/daily-essentials-duo',
  '/pdp/daily-multivitamin',
  '/pdp/vaginal-synbiotic',
  '/pdp/pds08-daily-kids-probiotic',
  '/pdp/sleep-restore',
];

const routesToHideFooter = ['/checkout'];
// A chapter runs edge to edge from its loading screen through the scroll
// experience — the site footer has no place in it.
const routePrefixesToHideFooter = ['/account/journey/'];

// Visually hidden until focused; first focusable element so keyboard users can
// bypass the header/nav and jump straight to <main id="main">. WCAG 2.4.1.
const SkipLink = styled.a`
  position: absolute;
  left: -9999px;
  top: var(--space-fixed-1, 8px);
  z-index: 100000;
  padding: 12px 20px;
  background: var(--fill-brand);
  color: var(--text-inverse-primary);
  border-radius: 8px;
  font-size: 14px;
  text-decoration: none;

  &:focus,
  &:focus-visible {
    left: var(--space-fixed-1, 8px);
    color: var(--text-inverse-primary);
    outline: 2px solid var(--fill-primary);
    outline-offset: 2px;
  }
`;

// The global span rule sets body type, so take the Banner's type instead.
const bannerCopy = css`
  font: inherit;
  letter-spacing: inherit;
  color: inherit;
`;

const DesktopOnly = styled.span`
  ${bannerCopy}

  @media ${mqLargeMax} {
    display: none;
  }
`;

const MobileOnly = styled.span`
  ${bannerCopy}

  @media ${mqLargeMin} {
    display: none;
  }
`;

interface HomeLayoutProps {
  children: React.ReactNode;
  /** Extra disclaimer lines rendered in the footer, supplied by the page's layout (e.g. PDP, journey). */
  footerDisclaimerConfig?: FooterDisclaimerConfig;
}

const HomeLayout = ({
  children,
  footerDisclaimerConfig = undefined,
}: HomeLayoutProps) => {
  const { userState } = useContext(UserContext);

  const loggedIn = !!userState?.email;
  const router = useRouter();
  const { showReferralBanner, referralSavingsLabel } = useReferralBanner();
  const { showSrpUpgradeBanner, srpSavingsLabel, isEligibilityPending } =
    useSrpUpgradeBanner();
  const [isMounted, setIsMounted] = useState(false);
  const [hpbSuppressed, setHpbSuppressed] = useState(false);

  useEffect(() => {
    const hasCookie = document.cookie.includes('hpb_suppress_banner=1');
    const hasParam = new URLSearchParams(window.location.search).has('hpb');

    if (hasCookie || hasParam) {
      setHpbSuppressed(true);
    }
    if (hasParam && !hasCookie) {
      const expires = new Date();
      expires.setDate(expires.getDate() + 3);
      document.cookie = `hpb_suppress_banner=1; expires=${expires.toUTCString()}; path=/`;
    }

    setIsMounted(true);
    const siteBody = document.querySelector('body');
    if (siteBody) {
      siteBody.classList.add('ready');
    }
  }, []);

  const getPromoBar = (loggedIn, routerPathName) => {
    // Suppress promo bar until client-side hydration is complete to prevent
    // server/client HTML mismatch (server always renders null since router.isReady=false)
    if (!isMounted) {
      return null;
    }

    // don't show banner on cart, checkout, or any journey chapter
    if (
      routesToHideBanner.includes(routerPathName) ||
      routePrefixesToHideBanner.some((prefix) =>
        routerPathName.startsWith(prefix),
      )
    ) {
      return null;
    }

    if (!router.isReady) {
      return null;
    }

    if (loggedIn) {
      // Hold the strip until eligibility resolves so a member upgrading to SRP
      // never watches the co-biotics banner swap out from under them.
      if (isEligibilityPending) {
        return null;
      }

      if (showSrpUpgradeBanner) {
        return (
          <Banner
            className='srp-promo-banner'
            message={`Save up to ${srpSavingsLabel}/year.`}
            actionText='Learn more'
            href={`${SRP_ENROLL_PATH}?plan=${SRP_PRODUCT_CODE}`}
            onClick={() =>
              trackEvent('Banner Clicked', { navLink: SRP_ENROLL_PATH })
            }
          />
        );
      }

      return (
        <Banner
          message='Members Only • 25% Off New Products'
          href='/new-products'
          onClick={() =>
            trackEvent('Banner Clicked', { navLink: '/new-products' })
          }
        />
      );
    }

    // hpb query param suppresses all non-member banners for logged-out users for 3 days
    if (!loggedIn && hpbSuppressed) {
      return null;
    }

    if (!loggedIn && showReferralBanner) {
      return (
        <Banner
          message={
            <>
              Save {referralSavingsLabel}: Your Referral Code Is Applied{' '}
              <Icon name='arrow-right' size='sm' />
            </>
          }
          href='/daily-synbiotic'
          onClick={() =>
            trackEvent('Banner Clicked', { navLink: '/daily-synbiotic' })
          }
        />
      );
    }

    // Logged out, Member LP always show new products banner
    if (!loggedIn && routerPathName === '/pdp/new-products') {
      return (
        <Banner
          message='Members Only • 25% Off New Products'
          href='/new-products'
          onClick={() =>
            trackEvent('Banner Clicked', { navLink: '/new-products' })
          }
        />
      );
    }

    const MiniSale = activeMiniSale();
    if (
      !loggedIn &&
      MiniSale &&
      isMiniSaleBannerHidden(MiniSale, router.asPath)
    ) {
      return null;
    }
    const bannerPath = MiniSale?.bannerUrl?.split(/[?#]/)[0];
    const currentPath = router.asPath.split(/[?#]/)[0];
    if (
      !loggedIn &&
      MiniSale &&
      bannerPath === currentPath &&
      !!getPromoCodeFromQueryParameters()
    ) {
      return null;
    }
    if (!loggedIn && MiniSale) {
      return (
        <Banner
          message={
            <>
              <Icon name='discount' size='sm' />
              {MiniSale.bannerCopy}
            </>
          }
          actionText='Shop Now'
          href={MiniSale.bannerUrl}
          onClick={() =>
            trackEvent('Banner Clicked', { navLink: MiniSale.bannerUrl })
          }
        />
      );
    }

    if (!loggedIn && router.asPath.split(/[?#]/)[0] === '/') {
      return (
        <Banner
          message={
            <>
              <DesktopOnly>
                25% off DS-01®: Find out if it’s right for you
              </DesktopOnly>
              <MobileOnly>25% off DS-01®: Take our quiz</MobileOnly>{' '}
              <Icon name='arrow-right' size='sm' />
            </>
          }
          href='/is-ds01-right-for-you'
          onClick={() =>
            trackEvent('Banner Clicked', {
              navLink: '/is-ds01-right-for-you',
            })
          }
        />
      );
    }

    if (!loggedIn && !routesToHideQuizBanner.includes(routerPathName)) {
      return (
        <Banner
          message={
            <>
              25% off: Find the right products for you{' '}
              <Icon name='arrow-right' size='sm' />
            </>
          }
          href='/find-your-routine'
          onClick={() =>
            trackEvent('Banner Clicked', { navLink: '/find-your-routine' })
          }
        />
      );
    }

    return null;
  };

  const currentPathname = router.asPath.split(/[?#]/)[0];
  const hideFooter =
    routesToHideFooter.includes(currentPathname) ||
    routePrefixesToHideFooter.some((prefix) =>
      currentPathname.startsWith(prefix),
    );

  return (
    <>
      <GlobalStyle
        $omitHydrationErrors={
          process.env.NEXT_PUBLIC_SUPPRESS_HYDRATION_ERRORS === 'true'
        }
      />
      <SkipLink href='#main'>Skip to main content</SkipLink>
      {getPromoBar(loggedIn, router.pathname)}
      <SiteHeader>
        <TopNavigation />
      </SiteHeader>
      <main id='main' className='site-content'>
        {children}
      </main>
      {!hideFooter ? (
        <SiteFooter>
          <FooterNavigation
            footerDisclaimerConfig={footerDisclaimerConfig}
            backgroundImage='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1752265586/library/footer/footer-fpo.jpg'
          />
        </SiteFooter>
      ) : null}
      <Cart />
    </>
  );
};

HomeLayout.defaultProps = {
  footerDisclaimerConfig: undefined,
};

export default HomeLayout;
